import { isLive, siteConfig } from '../data/siteConfig';
import type { FunnelData, Lang, QuoteResult } from '../types/funnel';
import { buildSmsConsentRecord } from '../data/consent';
import { newId } from './sandboxStore';
import { buildMoveSummary } from './moveSummary';
import { metaCookies } from './metaPixel';

/**
 * Frontend adapter for the booking backend (server interfaces from the spec).
 * With no apiBaseUrl configured, every call resolves to an honest preview result
 * or throws NotConnectedError — it never fabricates payment, signature or booking state.
 */
export class NotConnectedError extends Error {
  constructor(feature: string) {
    super(`${feature} is not connected`);
    this.name = 'NotConnectedError';
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${siteConfig.apiBaseUrl}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

export interface SubmitResult {
  mode: 'preview' | 'live';
  bookingId: string;
}

/** POST /api/booking-drafts — persists the draft, consent and attribution; returns booking ID + opaque resume token (cookie). */
export async function submitEstimateRequest(data: FunnelData, quote: QuoteResult, attribution: Record<string, string>, lang: Lang): Promise<SubmitResult> {
  if (!isLive()) {
    await new Promise((r) => setTimeout(r, 600));
    return { mode: 'preview', bookingId: newId('IM') };
  }
  const res = await post<{ bookingId: string }>('/api/booking-drafts', {
    data,
    // A2P SMS consent, each category independent. The server must stamp consent_timestamp (UTC) on receipt,
    // store this record with the lead and map both booleans to separate HighLevel fields. Never infer consent from the phone number.
    smsConsent: buildSmsConsentRecord(data.consentSmsMarketing, data.consentSmsNonMarketing, lang),
    // Voice / AI-call consent is separate from SMS.
    voiceConsent: data.consentAiCall === true,
    // Human-readable summary for HighLevel/SmartMoving text fields and the dispatcher.
    moveSummary: buildMoveSummary(data),
    quoteHash: quote.hash,
    configVersion: quote.configVersion,
    // Server recomputes the quote and coverage; the browser total is never trusted as the approved price.
    estimate: { crew: quote.crew.used, hourlyRate: quote.hourlyRate, laborLow: quote.laborLow, laborHigh: quote.laborHigh, zone: quote.zoneReasonKey, needsTransport: quote.needsTransport },
    attribution: { ...attribution, ...metaCookies() },
    // Lead event_id shared by the browser Pixel and server CAPI for deduplication.
    leadEventIdPrefix: 'lead_',
  });
  return { mode: 'live', bookingId: res.bookingId };
}

/** POST /api/deposit-checkout — server revalidates and returns a hosted Stripe Checkout URL for exactly $99 USD. */
export async function startDepositCheckout(bookingId: string, quoteHash: string): Promise<void> {
  if (!isLive() || !siteConfig.depositCheckoutEnabled) throw new NotConnectedError('Deposit checkout');
  const { url } = await post<{ url: string }>('/api/deposit-checkout', { bookingId, quoteHash });
  window.location.assign(url);
}

/** POST /api/contracts — returns signer-specific secure HighLevel document links. */
export async function requestContractLinks(bookingId: string): Promise<{ document: string; signerRole: string; url: string }[]> {
  if (!isLive() || !siteConfig.onlineSigningEnabled) throw new NotConnectedError('Online signing');
  return post('/api/contracts', { bookingId });
}

/**
 * POST /api/address-validation — server calls Google Address Validation API with private credentials.
 * Returns null in preview: the UI then keeps the address as "not verified — team reviews".
 */
export interface AddressValidationResult {
  verdict: 'validated' | 'needs_review';
  suggested?: { street: string; city: string; state: string; zip: string };
  issues: string[];
}
export async function validateAddress(addr: { street: string; unit: string; city: string; state: string; zip: string; placeId: string | null }): Promise<AddressValidationResult | null> {
  if (!isLive()) return null;
  return post<AddressValidationResult>('/api/address-validation', addr);
}

export const serverInterfaces = [
  { route: 'POST /api/booking-drafts', purpose: 'Create persisted draft + booking ID; store consent/attribution; set expiring opaque resume token.' },
  { route: 'PATCH /api/booking-drafts/:id', purpose: 'Authenticated, versioned updates; mark prior quote stale on relevant changes.' },
  { route: 'POST /api/address-validation', purpose: 'Validate selected normalized addresses; ZIP/street mismatch; confidence.' },
  { route: 'POST /api/quotes', purpose: 'Authoritative versioned estimate incl. extras and negotiation authorization.' },
  { route: 'GET /api/availability', purpose: 'Real dispatch availability or requested-time state.' },
  { route: 'POST /api/contracts', purpose: 'Only applicable signer-specific secure links and signing states.' },
  { route: 'POST /api/deposit-checkout', purpose: 'Revalidate; hosted Stripe Checkout, $99 USD, booking metadata, idempotency.' },
  { route: 'POST /api/webhooks/stripe', purpose: 'Verify signature; process once; persist; receipt/CRM/attribution.' },
  { route: 'POST /api/webhooks/signatures', purpose: 'Authenticate signature events; update exact document + signer; notify once.' },
  { route: 'GET /api/bookings/:id/status', purpose: 'Authenticated state, receipts and documents.' },
  { route: 'POST /api/crm/sync', purpose: 'HighLevel upsert + opportunity; mirrored SmartMoving during transition.' },
  { route: 'POST /api/voice/booking-context', purpose: 'Authenticated Kyle tool retrieves the matching draft.' },
];
